import React from "react";
import FontText from "../../FontText/FontText";
import { ThemeColorEnum } from "../../Themes/ThemeEnums";
import { FontSizeEnum, FontTypeEnum } from "../../FontText/FontTextEnums";
import AutoLayout from "../../Layouts/AutoLayout/AutoLayout";
import {
  AutoLayoutAlignEnum,
  AutoLayoutDirectionEnum,
  AutoLayoutResizingEnum,
} from "../../Layouts/AutoLayout/AutoLayoutEnums";
import ListItemTrailingElement from "./ListItemTrailingElement";
import ListItemLeadingElement from "./ListItemLeadingElement";
import styled, { css } from "styled-components";
import RippleEffect from "../../RippleEffect/RippleEffect";
import { RippleEffectShapeEnum } from "../../RippleEffect/RippleEffectShapeEnum";
import { ITheme } from "../../Themes/ThemeTypes";

const ClickableAutoLayout = styled(AutoLayout)<{
  theme: ITheme;
  $isClickable?: boolean;
}>`
  cursor: ${(props) => (props.$isClickable ? "pointer" : "auto")};

  ${(props) =>
    props.$isClickable &&
    css`
      &:hover {
        background-color: ${props.theme.colors.on_surface_variant_opacity_0_08};
      }
    `}
`;

const Clickable = styled.div<{
  theme: ITheme;
}>`
  width: 100%;
`;

interface ListItemProps {
  headline: string;
  supportingText?: string;
  children?: React.ReactNode;
  leadingElement?: React.ReactElement;
  trailingElement?: React.ReactElement | string;
  height?: number;
  onClick?: () => void;
}

const ListItem: React.FC<ListItemProps> = ({
  headline,
  supportingText,
  leadingElement,
  trailingElement,
  height,
  onClick,
}) => {
  const innerListItem = (
    <ClickableAutoLayout
      $backgroundColor={ThemeColorEnum.SURFACE}
      $direction={AutoLayoutDirectionEnum.HORIZONTAL}
      $horizontalResizing={AutoLayoutResizingEnum.FILL}
      $verticalResizing={AutoLayoutResizingEnum.FIXED}
      $align={AutoLayoutAlignEnum.LEFT}
      $topPadding={8}
      $bottomPadding={8}
      $leftPadding={16}
      $rightPadding={16}
      $height={height}
      $isClickable={onClick !== undefined}
    >
      {leadingElement && <ListItemLeadingElement>{leadingElement}</ListItemLeadingElement>}

      <AutoLayout
        $direction={AutoLayoutDirectionEnum.VERTICAL}
        $horizontalResizing={AutoLayoutResizingEnum.FILL}
        $verticalResizing={AutoLayoutResizingEnum.FILL}
        $align={AutoLayoutAlignEnum.LEFT}
      >
        <FontText
          text={headline}
          $color={ThemeColorEnum.ON_SURFACE}
          $fontType={FontTypeEnum.BODY}
          $fontSize={FontSizeEnum.LARGE}
        />
        {supportingText && (
          <FontText
            text={supportingText}
            $color={ThemeColorEnum.ON_SURFACE_VARIANT}
            $fontType={FontTypeEnum.BODY}
            $fontSize={FontSizeEnum.MEDIUM}
          />
        )}
      </AutoLayout>

      {trailingElement && (
        <ListItemTrailingElement>
          {typeof trailingElement === "string" ? (
            <FontText
              text={trailingElement}
              $color={ThemeColorEnum.ON_SURFACE_VARIANT}
              $fontType={FontTypeEnum.LABEL}
              $fontSize={FontSizeEnum.SMALL}
            />
          ) : (
            trailingElement
          )}
        </ListItemTrailingElement>
      )}
    </ClickableAutoLayout>
  );

  if (!onClick) {
    return innerListItem;
  }

  return (
    <Clickable onClick={onClick}>
      <RippleEffect shape={RippleEffectShapeEnum.RECTANGLE} width="100%">
        {innerListItem}
      </RippleEffect>
    </Clickable>
  );
};

export default ListItem;
